// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@rc/XDL/Helpers";
import { color } from "@rc/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    productsWrapper?: InjectableCss;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 0 0 35px 0;
            cursor: pointer;
            width: 33.33%;
            ${screen("lte", "md")`
                width: 50%;
            `};
            ${screen("lte", "sm")`
                width: 100%;
            `};
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            width: 33.33%;
            ${screen("lte", "md")`
                width: 50%;
            `};
            ${screen("lte", "sm")`
                width: 100%;
            `};
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 72px;
            max-height: 72px;
            flex-basis: 72px;
            &&& {
                margin-right: 5px;
            }
        `,
    },
    image: {
        width: "72px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 87px);
            flex-basis: calc(100% - 87px);
        `,
    },
    titleLink: {
        typographyProps: {
            size: 16,
            weight: 500,
            lineHeight: "17px",
            ellipsis: false,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                &&& {
                    color: ${color("common.ui6")};
                }
            `,
        },
    },
    erpNumberText: {
        color: "text.main",
        size: 12,
        lineHeight: "13px",
        ellipsis: true,
        css: css`
            width: auto;
            max-width: 205px;
            text-align: left;
            margin: 7px 0 0 0;
        `,
    },
    productsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <StyledWrapper {...styles.productsWrapper}>
                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                            <LazyImage {...styles.image} src={product.image} className="autocomplete-products-image" />
                        </GridItem>
                        <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link
                                {...styles.titleLink}
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                {product.displayTitle}
                            </Link>
                            <Typography
                                {...styles.erpNumberText}
                                data-test-selector="autocompleteProducts_PartNumber"
                                className="autocomplete-products-erp-number"
                            >
                                {product.isNameCustomerOverride ? (
                                    <>
                                        {translate("My Part #")}:&nbsp;
                                        {product.displayName}
                                    </>
                                ) : (
                                    <>
                                        {translate("SKU")}:&nbsp;
                                        {product.displayErpNumber}
                                    </>
                                )}
                                {product.manufacturerItemNumber && (
                                    <>
                                        &nbsp;
                                        {translate("MFG #")}:&nbsp;
                                        {product.displayManufacturerItemNumber}
                                    </>
                                )}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                ))}
            </StyledWrapper>
        );
    }
}

export default AutocompleteProducts;
